import { Injectable } from '@angular/core';
import { Observable, catchError, map } from 'rxjs';
import { HttpService } from '../http.service';
import { HttpErrorHandlerService } from '../http-error-handler.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RefundService {

  constructor(
    private httpService: HttpService,
    private errorHandler: HttpErrorHandlerService
  ) { }

  /**
   * Obtiene una lista de motivos de devolución disponibles
   * optimizada para mostrar en componentes select
   * @returns Observable con un array de motivos de devolución en formato {id, name}
   */
  getRefundReasons(): Observable<any[]> {
    return this.httpService.get(`refund-reasons`).pipe(
      map((response: any) => {
        return response.data.map((reason: any) => ({
          id: reason.id,
          key: reason.key,
          name: reason.name
        })) || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Solicita una devolución al servidor
   * @param userId ID del usuario para el que se solicita la devolución
   * @param data Datos adicionales de la devolución (motivo, descripción, etc.)
   * @returns Observable con la respuesta del servidor
   */
  requestRefund(userId: number, data?: any): Observable<any> {
    const requestData = {
      user_id: userId,
      ...data
    };

    return this.httpService.post(`refund-request`, requestData).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Solicita una devolución del pago de chat premium asociado a una conversación.
   */
  requestPremiumChatRefund(conversationId: number): Observable<any> {
    return this.httpService.post(`payment/premium-chat/${conversationId}/refund-request`, {}).pipe(
      map((response: any) => response),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene el historial de devoluciones del usuario actual
   * @returns Observable con un array de devoluciones
   */
  getRefundHistory(): Observable<any[]> {
    return this.httpService.get(`refund-history`).pipe(
      map((response: any) => {
        return response.data || [];
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene el estado de una devolución específica
   * @param refundId ID de la devolución
   * @returns Observable con los detalles de la devolución
   */
  getRefundStatus(refundId: number): Observable<any> {
    return this.httpService.get(`refund-status/${refundId}`).pipe(
      map((response: any) => {
        return response.data;
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Cancela una solicitud de devolución pendiente
   * @param refundId ID de la devolución a cancelar
   * @returns Observable con la respuesta del servidor
   */
  cancelRefund(refundId: number): Observable<any> {
    return this.httpService.delete(`refund-cancel/${refundId}`).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }
}
